import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Button,
  Collapse
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  BugReport as BugReportIcon,
  Assessment as AssessmentIcon,
  Event as EventIcon,
  VerifiedUser as VerifiedUserIcon,
  Shield as ShieldIcon,
  CloudDownload as DeployIcon,
  NetworkCheck as NetworkIcon,
  Language as DomainIcon,
  Logout as LogoutIcon,
  FindInPage as SysScanIcon,
  VpnKey as VpnKeyIcon,
  ExpandLess,
  ExpandMore,
  Sensors as WazuhIcon,
  Analytics as AnalyticsIcon,
  CloudQueue as DevOpsIcon,
  Inventory as DockerIcon,
  Router as RouterIcon
} from '@mui/icons-material';
import { RocketLaunch as ManageServiceIcon } from '@mui/icons-material';
import { Terminal as TerminalIcon } from '@mui/icons-material';
import { MonitorHeart as MonitoringIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 280;

const menuStructure = [
  {
    text: 'Overview',
    icon: <DashboardIcon />,
    path: '/',
    active: true
  },
  {
    text: 'Wazuh',
    icon: <WazuhIcon />,
    active: true,
    subItems: [
      {
        text: 'Agents',
        icon: <PeopleIcon />,
        path: '/agents'
      },
      {
        text: 'Groups',
        icon: <PeopleIcon />,
        path: '/groups'
      },
      {
        text: 'Compliance',
        icon: <AssessmentIcon />,
        path: '/compliance'
      },
      {
        text: 'Security Alerts',
        icon: <SecurityIcon />,
        path: '/alerts'
      },
      {
        text: 'Events',
        icon: <EventIcon />,
        path: '/events'
      },
      {
        text: 'MITRE ATT&CK',
        icon: <ShieldIcon />,
        path: '/mitre-attack'
      },
      {
        text: 'SCA',
        icon: <VerifiedUserIcon />,
        path: '/sca'
      },
      {
        text: 'CVE Detection',
        icon: <BugReportIcon />,
        path: '/vulnerabilities'
      },
      {
        text: 'SysScan',
        icon: <SysScanIcon />,
        path: '/sysscan'
      },
      {
        text: 'SSH Activity',
        icon: <VpnKeyIcon />,
        path: '/ssh-activity'
      },
      {
        text: 'Docker Info',
        icon: <DockerIcon />,
        path: '/docker-info'
      },
      {
        text: 'Deploy New Agent',
        icon: <DeployIcon />,
        path: '/deploy-agent'
      }
    ]
  },
  {
    text: 'CloudsAnalytics - Security Analysis',
    icon: <AnalyticsIcon />,
    active: true,
    subItems: [
      {
        text: 'Network Discovery',
        icon: <NetworkIcon />,
        path: '/network-discovery'
      },
      {
        text: 'Domain Scanner',
        icon: <DomainIcon />,
        path: '/subdomain-scanner'
      },
      {
        text: 'WAF Scanner',
        icon: <ShieldIcon />,
        path: '/waf-scanner'
      }
    ]
  },
  {
    text: 'DevOps Management',
    icon: <DevOpsIcon />,
    active: true,
    subItems: [
      {
        text: 'Setup Infrastructure',
        icon: <NetworkIcon />,
        path: '/setup-infrastructure'
      },
      {
        text: 'Manage Service',
        icon: <ManageServiceIcon />,
        path: '/manage-service'
      },
      {
        text: 'Execute Commands',
        icon: <TerminalIcon />,
        path: '/execute-custom-commands'
      },
      {
        text: 'DevOps Monitoring',
        icon: <MonitoringIcon />,
        path: '/devops-monitoring'
      },
      {
        text: 'Remote Connections',
        icon: <RouterIcon />,
        path: '/remote-connections'
      }
    ]
  }
];

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [openMenus, setOpenMenus] = useState({});

  const handleMenuClick = (path) => {
    navigate(path);
  };

  const handleToggleMenu = (menuText) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuText]: !prev[menuText]
    }));
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <Drawer
      variant="permanent"
      className="sidebar"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box className="sidebar-header">
        <SecurityIcon className="sidebar-logo" />
        <Box>
          <Typography variant="h6" className="sidebar-title">
            OpenShield
          </Typography>
          <Typography variant="body2" className="sidebar-subtitle">
            Dashboard
          </Typography>
        </Box>
      </Box>
      
      <Divider />
      
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <List className="sidebar-menu" sx={{ flex: 1 }}>
          {menuStructure.map((item) => (
            <React.Fragment key={item.text}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={item.path && ((item.path === '/' && location.pathname === '/') ||
                    (item.path !== '/' && location.pathname.startsWith(item.path)))}
                  onClick={() => item.subItems ? handleToggleMenu(item.text) : handleMenuClick(item.path)}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '& *': {
                      pointerEvents: 'none'
                    }
                  }}
                >
                  <ListItemIcon className="sidebar-menu-icon">
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    className="sidebar-menu-text"
                  />
                  {item.subItems && (
                    openMenus[item.text] ? <ExpandLess sx={{ pointerEvents: 'auto' }} /> : <ExpandMore sx={{ pointerEvents: 'auto' }} />
                  )}
                </ListItemButton>
              </ListItem>
              
              {/* Render submenu items */}
              {item.subItems && (
                <Collapse in={openMenus[item.text]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItem key={subItem.text} disablePadding>
                        <ListItemButton
                          selected={(subItem.path === '/' && location.pathname === '/') ||
                            (subItem.path !== '/' && location.pathname.startsWith(subItem.path))}
                          onClick={() => handleMenuClick(subItem.path)}
                          sx={{
                            pl: 4,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '& *': {
                              pointerEvents: 'none'
                            }
                          }}
                        >
                          <ListItemIcon className="sidebar-menu-icon" sx={{ minWidth: 40 }}>
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={subItem.text}
                            className="sidebar-menu-text"
                            primaryTypographyProps={{ fontSize: '0.9rem' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
        
        {/* Logout Button at Bottom */}
        {user && (
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button
              variant="text"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{ width: '100%', fontSize: '1rem', color: 'white' }}
            >
              Logout
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
